import { useEffect, useRef, useState } from 'react';
import { cn } from '@jeraisonne/ui';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { groupFeedItems, groupToggleLabel, type FeedGroup } from '@/lib/news/feed-groups';
import { AssignmentCard } from './assignment-card';
import { ChallengeCard } from './challenge-card';
import { AttemptCard } from './attempt-card';
import { BlogArticleCard } from './blog-article-card';
import { DiscussionCard } from './discussion-card';
import { SubmissionCard } from './submission-card';
import { VisioCard } from './visio-card';
import { CardAssignmentCard } from './card-assignment-card';
import { EvaluationGradeCard } from './evaluation-grade-card';
import { DiagnosticFeedCard } from './diagnostic-feed-card';
import { QuestionReportCard } from './question-report-card';
import { DailyRecapCard } from './daily-recap-card';
import { PrivateLiveCard } from './private-live-card';
import { EmptyFeed } from './empty-feed';
import type { AssignmentRow, AuthorsById, FeedItem, ViewerRole } from './types';

export function NewsFeed({
  items,
  isLoading,
  viewerRole,
  authorsById,
  doneAssignmentKeys,
  onStartAssignment,
  startingAssignmentId = null,
  viewerId,
  isUnread,
  hasMore = false,
  isFetchingMore = false,
  onLoadMore,
}: {
  items: FeedItem[];
  isLoading: boolean;
  viewerRole: ViewerRole;
  authorsById: AuthorsById;
  doneAssignmentKeys?: Set<string>;
  onStartAssignment?: (a: AssignmentRow) => void;
  /** Devoir en cours d'ouverture (spinner sur son bouton). */
  startingAssignmentId?: string | null;
  /** Utilisateur courant (liens « page élève », cf. studentLinkFor). */
  viewerId?: string | null;
  /** Élément arrivé depuis la dernière ouverture du Feed → marquage discret. */
  isUnread?: (item: FeedItem) => boolean;
  /** Il reste des éléments plus anciens (chargement progressif au défilement). */
  hasMore?: boolean;
  isFetchingMore?: boolean;
  onLoadMore?: () => void;
}) {
  // Groupes dépliés (clé = 1er élément du groupe).
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="border-primary-400 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
      </div>
    );
  }

  if (items.length === 0) {
    return <EmptyFeed role={viewerRole} />;
  }

  return (
    <div className="space-y-4">
      {groupFeedItems(items).map((group) => renderGroup(group))}
      {onLoadMore && (hasMore || isFetchingMore) && (
        <LoadMoreSentinel loading={isFetchingMore} hasMore={hasMore} onLoadMore={onLoadMore} />
      )}
    </div>
  );

  function renderItem(item: FeedItem) {
    return (
      <UnreadFrame key={feedItemKey(item)} unread={isUnread?.(item) ?? false}>
        {renderCard(item)}
      </UnreadFrame>
    );
  }

  /**
   * Activités successives d'une même personne : la plus récente visible, les
   * autres repliées derrière « + N autres activités de … » (déplier / replier).
   */
  function renderGroup(group: FeedGroup) {
    const [head, ...rest] = group.items;
    if (!head) return null;
    if (rest.length === 0) return renderItem(head);
    const key = feedItemKey(head);
    const open = expanded.has(key);
    const name = (group.actorId && authorsById.get(group.actorId)?.display_name) || 'Élève';
    const hiddenUnread = rest.filter((i) => isUnread?.(i) ?? false).length;
    const toggle = () => {
      if (open) scrollToGroupHead(key);
      setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
      if (open) {
        // « Replier » disparaît : le focus revient sur le paquet.
        requestAnimationFrame(() =>
          document
            .querySelector<HTMLElement>(`[data-group-key="${CSS.escape(key)}"] [data-group-toggle]`)
            ?.focus({ preventScroll: true }),
        );
      }
    };
    const label = groupToggleLabel(rest.length, name);
    // Hauteur animée (grille 0fr ↔ 1fr) : les cartes cachées restent montées,
    // inaccessibles tant que le paquet est replié.
    const fold = (shown: boolean) =>
      cn(
        'grid transition-[grid-template-rows,opacity] duration-300 ease-out motion-reduce:transition-none',
        shown ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
      );
    return (
      <div
        key={`group-${key}`}
        data-testid="feed-group"
        data-group-key={key}
        className="scroll-mt-24"
      >
        {renderItem(head)}

        {/* Les autres activités : glissent sous la 1re carte. */}
        <div className={fold(open)} aria-hidden={!open} inert={!open} data-testid="feed-group-rest">
          <div className="min-h-0 overflow-hidden">
            <div className="space-y-3 pt-3">
              {rest.map((item, i) => (
                <div
                  key={feedItemKey(item)}
                  className={cn(
                    'transition-transform duration-300 ease-out motion-reduce:transition-none',
                    open ? 'translate-y-0' : '-translate-y-3',
                  )}
                  style={{ transitionDelay: open ? `${Math.min(i, 5) * 40}ms` : '0ms' }}
                >
                  {renderItem(item)}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Replié : les suivantes empilées sous la 1re, comme un paquet. */}
        <div className={fold(!open)} aria-hidden={open} inert={open}>
          <div className="min-h-0 overflow-hidden">
            <button
              type="button"
              aria-expanded={false}
              aria-label={`Afficher ${label}`}
              data-group-toggle
              onClick={toggle}
              className="group block w-full pb-1 active:scale-[0.99]"
            >
              <span
                aria-hidden
                className="ocean:ring-white/10 ring-ink-100 group-hover:bg-primary-50 ocean:group-hover:bg-white/15 mx-3 flex min-h-[2.75rem] items-center justify-center gap-1.5 rounded-b-2xl bg-white px-3 pb-1 pt-1.5 shadow-sm ring-1 transition-colors"
              >
                <span className="text-primary-600 ocean:text-primary-300 font-sans text-sm font-semibold">
                  {label}
                </span>
                {hiddenUnread > 0 && (
                  <span className="bg-accent-400 text-ink-900 rounded-full px-2 py-0.5 text-xs font-bold">
                    {hiddenUnread} nouvelle{hiddenUnread > 1 ? 's' : ''}
                  </span>
                )}
                <ChevronDown className="text-primary-600 ocean:text-primary-300 h-4 w-4" />
              </span>
              {rest.length > 1 && (
                <span
                  aria-hidden
                  className="ocean:ring-white/10 ring-ink-100 mx-7 block h-2.5 rounded-b-xl bg-white opacity-70 shadow-sm ring-1"
                />
              )}
            </button>
          </div>
        </div>

        {/* Déplié : refermer le paquet. */}
        <div className={fold(open)} aria-hidden={!open} inert={!open}>
          <div className="min-h-0 overflow-hidden">
            <button
              type="button"
              aria-expanded
              onClick={toggle}
              className="text-primary-600 ocean:text-primary-300 mt-3 flex min-h-[2.75rem] w-full items-center justify-center gap-1.5 rounded-xl font-sans text-sm font-semibold active:scale-[0.98]"
            >
              <ChevronUp className="h-4 w-4" aria-hidden /> Replier
            </button>
          </div>
        </div>
      </div>
    );
  }

  function renderCard(item: FeedItem) {
    if (item.kind === 'private_live') {
      return <PrivateLiveCard data={item.data} />;
    }
    if (item.kind === 'daily_recap') {
      return <DailyRecapCard data={item.data} />;
    }
    if (item.kind === 'question_report') {
      return <QuestionReportCard data={item.data} viewerId={viewerId} />;
    }
    if (item.kind === 'diagnostic') {
      return (
        <DiagnosticFeedCard
          data={item.data}
          viewerRole={viewerRole === 'tutor' ? 'tutor' : 'parent'}
        />
      );
    }
    if (item.kind === 'evaluation') {
      return <EvaluationGradeCard data={item.data} viewerRole={viewerRole} viewerId={viewerId} />;
    }
    if (item.kind === 'card_assignment') {
      return <CardAssignmentCard data={item.data} />;
    }
    if (item.kind === 'visio') {
      return <VisioCard data={item.data} viewerId={viewerId} />;
    }
    if (item.kind === 'submission') {
      return <SubmissionCard data={item.data} viewerRole={viewerRole} />;
    }
    if (item.kind === 'assignment') {
      const doneKey = `${item.data.student_id}:${item.data.exercise_id}`;
      return (
        <AssignmentCard
          data={item.data}
          viewerRole={viewerRole}
          authorsById={authorsById}
          isDone={doneAssignmentKeys?.has(doneKey) ?? false}
          isStarting={startingAssignmentId === item.data.id}
          startDisabled={startingAssignmentId !== null}
          onStart={
            viewerRole === 'student' && onStartAssignment
              ? () => onStartAssignment(item.data)
              : undefined
          }
        />
      );
    }
    if (item.kind === 'challenge') {
      const author = authorsById.get(item.data.user_id);
      const isTutorAuthor = author?.role === 'tutor' || author?.role === 'admin';
      return (
        <ChallengeCard
          data={item.data}
          authorName={author?.display_name ?? 'Élève'}
          authorAvatar={author?.avatar_url}
          authorTutorId={isTutorAuthor ? item.data.user_id : null}
          viewerId={viewerId}
        />
      );
    }
    if (item.kind === 'blog') {
      return <BlogArticleCard data={item.data} />;
    }
    if (item.kind === 'discussion') {
      return <DiscussionCard data={item.data} viewerRole={viewerRole} />;
    }
    const author = authorsById.get(item.data.student_id);
    return (
      <AttemptCard
        data={item.data}
        authorName={author?.display_name ?? 'Élève'}
        authorAvatar={author?.avatar_url}
        viewerRole={viewerRole}
        viewerId={viewerId}
      />
    );
  }
}

/**
 * En repliant, ramène doucement la 1re carte du groupe à l'écran si elle en
 * est sortie (sinon on se retrouve plus bas, perdu dans le fil).
 */
function scrollToGroupHead(key: string) {
  if (typeof document === 'undefined') return;
  const el = document.querySelector<HTMLElement>(`[data-group-key="${CSS.escape(key)}"]`);
  if (!el || typeof el.scrollIntoView !== 'function') return;
  const top = el.getBoundingClientRect().top;
  if (top >= 0 && top < window.innerHeight * 0.6) return;
  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  el.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
}

function feedItemKey(item: FeedItem): string {
  const prefix = {
    assignment: 'a',
    challenge: 'c',
    blog: 'b',
    discussion: 'd',
    submission: 's',
    visio: 'v',
    attempt: 'e',
    attempt_started: 'p',
    card_assignment: 'k',
    evaluation: 'n',
    diagnostic: 'g',
    question_report: 'r',
    daily_recap: 'j',
    private_live: 'l',
  }[item.kind];
  return `${prefix}-${item.data.id}`;
}

/**
 * Marquage DISCRET d'un élément nouveau (arrivé depuis la dernière ouverture du
 * Feed) : fin liseré doré + point en haut à gauche. Ne touche pas aux cartes.
 */
function UnreadFrame({ unread, children }: { unread: boolean; children: React.ReactNode }) {
  if (!unread) return <>{children}</>;
  return (
    <div className="ring-accent-400/40 relative rounded-2xl ring-1" data-unread="true">
      <span
        role="img"
        aria-label="Nouveau"
        className="bg-accent-400 light:shadow-[0_0_0_2px_#ffffff] absolute -left-1 -top-1 z-10 h-2.5 w-2.5 rounded-full shadow-[0_0_0_2px_rgba(8,42,92,0.9)]"
      />
      {children}
    </div>
  );
}

/**
 * Bas du fil : charge la suite quand il approche de l'écran (marge de 600 px),
 * puis recommence tant qu'il reste visible. Sans IntersectionObserver
 * (vieux navigateur), un bouton « Voir plus » prend le relais.
 */
function LoadMoreSentinel({
  loading,
  hasMore,
  onLoadMore,
}: {
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  const supported = typeof IntersectionObserver !== 'undefined';

  useEffect(() => {
    const el = ref.current;
    if (!el || !supported) return;
    const observer = new IntersectionObserver(
      (entries) => setInView(entries.some((e) => e.isIntersecting)),
      { rootMargin: '600px 0px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [supported]);

  useEffect(() => {
    if (inView && hasMore && !loading) onLoadMore();
  }, [inView, hasMore, loading, onLoadMore]);

  return (
    <div ref={ref} className="flex min-h-[2.75rem] items-center justify-center py-2">
      {loading ? (
        <div
          role="status"
          aria-label="Chargement de la suite"
          className="border-primary-400 h-6 w-6 animate-spin rounded-full border-2 border-t-transparent"
        />
      ) : !supported && hasMore ? (
        <button
          type="button"
          onClick={onLoadMore}
          className="text-primary-600 ocean:text-primary-300 min-h-[2.75rem] rounded-full px-5 font-sans text-sm font-semibold active:scale-[0.98]"
        >
          Voir plus
        </button>
      ) : null}
    </div>
  );
}
